
import Vue from "vue";
import config from "@/config";

const CHATBASE_DOMAIN = "www.chatbase.co";
const CHATBASE_SCRIPT_SRC = `https://${CHATBASE_DOMAIN}/embed.min.js`;

export default Vue.extend({
  name: "ChatbaseWidget",
  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    userContext: {
      type: Object, // ChatbaseUserContext
      default: () => null
    }
  },
  data() {
    return {
      isReady: false,
      hasErrored: false,
      initializeTimer: null as ReturnType<typeof setTimeout> | null,
      hasInitialized: false
    };
  },
  computed: {
    isFeatureEnabled(): boolean {
      // Check config or env var if available
      const flag =
        (config as any).CHATBASE_ENABLED ||
        process.env.VUE_APP_CHATBASE_ENABLED;
      if (typeof flag === "string") {
        return flag.toLowerCase() === "true";
      }
      // Default to true if not explicitly disabled
      return true;
    }
  },
  watch: {
    disabled(newVal) {
      if (!this.isFeatureEnabled || newVal) return;
      if (this.isChatbaseAvailable()) {
        this.markReady();
      }
    },
    userContext: {
      deep: true,
      handler(newVal) {
        if (!this.isReady || !newVal) return;
        this.identifyUser(newVal);
      }
    }
  },
  mounted() {
    if (!this.isFeatureEnabled || this.disabled) {
      return;
    }

    if (this.isChatbaseAvailable()) {
      this.markReady();
      this.hasInitialized = true;
      return;
    }

    if (this.hasInitialized) return;
    this.hasInitialized = true;

    this.scheduleInitialization();
  },
  beforeDestroy() {
    if (this.initializeTimer) {
      clearTimeout(this.initializeTimer);
    }
  },
  methods: {
    isChatbaseAvailable() {
      return (
        typeof window !== "undefined" &&
        !!(window as any).chatbase &&
        typeof (window as any).chatbase.open === "function"
      );
    },

    markReady() {
      if (!this.isReady) {
        this.isReady = true;
        this.hasErrored = false;
        this.$emit("load");
        // If user context is already available, identify
        if (this.userContext) {
          this.identifyUser(this.userContext);
        }
      }
    },

    markError(error: Error) {
      if (!this.hasErrored) {
        this.hasErrored = true;
        this.$emit("error", error);
      }
    },

    identifyUser(context: any) {
      try {
        (window as any).chatbase?.identify?.({
          user_id: context.userId,
          user_hash: context.userHash,
          user_metadata: context.metadata
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn("Unable to pass user context to Chatbase:", error);
      }
    },

    initializeChatbase() {
      if (this.isChatbaseAvailable()) {
        this.markReady();
        return;
      }

      // Get chatbot ID from config or fallback
      const chatbotId =
        (config as any).CHATBASE_CHATBOT_ID ||
        process.env.VUE_APP_CHATBASE_CHATBOT_ID ||
        "PRDTKfv5PscPBi3CxLxJW";

      if (!chatbotId) {
        const error = new Error(
          "Chatbase chatbot ID missing. Check config or VUE_APP_CHATBASE_CHATBOT_ID."
        );
        // eslint-disable-next-line no-console
        console.warn(error.message);
        this.markError(error);
        return;
      }

      // Configure the widget before the script loads
      (window as any).embeddedChatbotConfig = {
        chatbotId,
        domain: CHATBASE_DOMAIN
      };

      if (this.userContext) {
        (window as any).chatbaseUserConfig = {
          user_id: this.userContext.userId,
          user_hash: this.userContext.userHash,
          user_metadata: this.userContext.metadata
        };
      }

      // Check if the script already exists in the DOM
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[src="${CHATBASE_SCRIPT_SRC}"]`
      );

      const script = existingScript ?? document.createElement("script");

      const attachListeners = () => {
        script.onload = () => {
          this.markReady();
        };
        script.onerror = () => {
          const scriptError = new Error(
            "Failed to load Chatbase widget script"
          );
          // eslint-disable-next-line no-console
          console.error(scriptError.message);
          this.markError(scriptError);
        };
      };

      if (!existingScript) {
        script.src = CHATBASE_SCRIPT_SRC;
        script.defer = true;
        script.async = true;
        script.setAttribute("data-chatbase", "true");
        script.setAttribute("chatbotId", chatbotId);
        script.setAttribute("domain", CHATBASE_DOMAIN);
        attachListeners();
        document.body.appendChild(script);
      } else {
        attachListeners();
      }
    },

    scheduleInitialization() {
      // dashboard-admin is web-only, but check cordova just in case (though unlikely for admin dashboard)
      if ((window as any).cordova) {
        document.addEventListener(
          "deviceready",
          this.initializeChatbase,
          false
        );
        return;
      }

      this.initializeTimer = setTimeout(this.initializeChatbase, 1500);
    }
  }
});
